import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePermanentRealTimeUser } from "hooks";
import { setCartIndicatorsToFilter, useCartIndicatorsToFilter } from "modules/Appointments/hooks";
import { APPOINTMENTS_STORAGE_KEYS } from "modules/Appointments/storageKeys";
import { indicatorsFiltrationLogic, initializeIdentifiers } from "modules/Appointments/util";

import type { IndicatorFilters } from "modules/Appointments/components";
import type { CartIndicatorType } from "modules/Appointments/queryKeys";

const useGetCartOptions = (specialIndicatorsData?: CartIndicatorType) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();

  const isShopCartEnabled = !(
    user?.shop_status_same_day_filter_enabled ||
    user?.shop_status_next_day_filter_enabled ||
    user?.shop_status_to_order_filter_enabled ||
    user?.shop_status_backorder_filter_enabled ||
    user?.shop_status_arrived_parts_filter_enabled
  );

  const cartIndicators: IndicatorFilters[] = [
    ...(isShopCartEnabled ? [{ icon: "is_shop", active: !!specialIndicatorsData?.is_shop, filter: "is_shop" }] : []),
    ...(user?.shop_status_same_day_filter_enabled
      ? [
          {
            icon: "is_shop",
            active: !!specialIndicatorsData?.is_shop_green,
            filter: "is_shop_green",
            borderColor: "green",
            text: t("v8_shopping_cart_same_day", "Same day")
          }
        ]
      : []),
    ...(user?.shop_status_next_day_filter_enabled
      ? [
          {
            icon: "is_shop",
            active: !!specialIndicatorsData?.is_shop_orange,
            filter: "is_shop_orange",
            borderColor: "orange",
            text: t("v8_shopping_cart_tomorrow", "Tomorrow")
          }
        ]
      : []),
    ...(user?.shop_status_to_order_filter_enabled
      ? [
          {
            icon: "is_shop",
            active: !!specialIndicatorsData?.is_shop_blue,
            filter: "is_shop_blue",
            borderColor: "blue",
            text: t("v8_shopping_cart_to_order", "To order")
          }
        ]
      : []),
    ...(user?.shop_status_backorder_filter_enabled
      ? [
          {
            icon: "is_shop",
            active: !!specialIndicatorsData?.is_shop_red,
            filter: "is_shop_red",
            borderColor: "red",
            text: t("v8_shopping_cart_backorder", "Backorder")
          }
        ]
      : []),
    ...(user?.shop_status_arrived_parts_filter_enabled
      ? [
          {
            icon: "is_shop",
            active: !!specialIndicatorsData?.is_shop_yellow,
            filter: "is_shop_yellow",
            borderColor: "yellow",
            text: t("v8_arrived", "Arrived")
          }
        ]
      : [])
  ];

  return { cartIndicators };
};

//TODO: Revisit this hook and useGetStarIndicators, the second one was following the implementation of the first one,
// look into how to improve filtering in general here
export const useGetCartIndicators = () => {
  const specialIndicatorsData = useCartIndicatorsToFilter();
  const { cartIndicators } = useGetCartOptions(specialIndicatorsData);
  const [cartFilters, setCartFilters] = useState<IndicatorFilters[]>(initializeIdentifiers(cartIndicators, APPOINTMENTS_STORAGE_KEYS.CartIndicatorPreference));

  useEffect(() => {
    const updateIndicatorsQueryData = () => {
      const indicatorFiltersObj = cartFilters.filter(item => item.active && item.filter).reduce((acc, item) => ({ ...acc, [item.filter]: item.filter }), {});

      if (JSON.stringify(specialIndicatorsData) !== JSON.stringify(indicatorFiltersObj)) {
        setCartIndicatorsToFilter(indicatorFiltersObj);
      }
    };

    updateIndicatorsQueryData();
  }, [cartFilters]);

  const handleCartFilters = (index: number) => {
    setCartFilters(current => indicatorsFiltrationLogic(current, index, APPOINTMENTS_STORAGE_KEYS.CartIndicatorPreference));
  };

  return { cartFilters, handleCartFilters };
};
